import { compact } from 'lodash'
import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { currencyFloat, formatDuration } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Gift } from './Gift'
import { Item } from './Item'
import { Option } from './Option'
import { Promotion } from './Promotion'
import { FKType, SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Source } from './Source'
import { Subscription } from './Subscription'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'
import { User } from './User'

type PackageConstructorArgs = ConstructorArgs<() => Package>

export class Package extends Transaction implements Transactable {
    readonly recordType = 'package'

    readonly iTransactable = true

    allowSurcharge?: boolean
    maxPaymentCount?: number // Integer
    minPaymentPercent?: number // Integer
    memo?: string
    status?: string // only settable for new packages [sentQuote, paid] and used to log active packages
    expires?: number // Integer
    capacity?: number // Integer
    capacityType?: string
    markPaidDate?: moment.Moment
    expiredDate?: moment.Moment
    completedDate?: moment.Moment

    private readonly _paidAmount?: number

    readonly lastPaymentDate?: moment.Moment
    readonly remainingBalance?: number
    readonly remainingPaymentCount?: number

    static readonly DEFAULT_EXPIRES = 30 // Integer
    static readonly DEFAULT_REMINDER = '1'
    static readonly PACKAGE_CAPACITY = 100
    static readonly PACKAGE_TOTAL_LIMIT = 100000

    constructor(data?: Partial<PackageConstructorArgs>, params?: { item?: Item }) {
        super(data)
        if (!data) {
            if (params?.item) {
                this.item = params.item
                this.expires = params.item.expires
                this.capacity = params.item.capacity
                this.total = params.item.deposit
            }
            else {
                this.expires = Package.DEFAULT_EXPIRES
            }
            this.reminder = Package.DEFAULT_REMINDER
            return
        }

        this.allowSurcharge = data.allow_surcharge ?? false
        this.maxPaymentCount = this.propertyFromIntArg(data.max_payment_count)
        this.minPaymentPercent = this.propertyFromIntArg(data.min_payment_percent)
        this.expires = this.propertyFromIntArg(data.expires)
        this.reminder = data.reminder ?? undefined
        this.memo = data.memo ?? undefined
        this.capacity = this.propertyFromIntArg(data.capacity)
        this.capacityType = data.capacity_type ?? undefined
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.expiredDate = this.propertyFromTimestampArg(data.expired_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.status =
            this.paidDate !== undefined
                ? 'paid'
                : this.cancelledDate !== undefined
                  ? 'canceled'
                  : 'sentQuote'

        this._paidAmount = this.propertyFromCurrencyArg(data.paid_amount)

        this.lastPaymentDate = this.propertyFromTimestampArg(data.last_payment_date)
        this.remainingBalance = this.propertyFromCurrencyArg(data.remaining_balance)
        this.remainingPaymentCount = this.propertyFromIntArg(data.remaining_payment_count)
    }

    clone(overrides?: Partial<PackageConstructorArgs>): Package {
        return new Package({ ...this.serializeForClone(), ...overrides })
    }

    static initWithItem(item: Item): Package {
        return new Package(undefined, { item })
    }

    static expirationStrings(): Option[] {
        const strings: Option[] = [
            new Option(undefined, { id: '7', label: '1 week' }),
            new Option(undefined, { id: '10', label: '10 days' }),
            new Option(undefined, { id: '14', label: '2 weeks' }),
            new Option(undefined, { id: '21', label: '3 weeks' }),
            new Option(undefined, { id: '28', label: '4 weeks' }),
            new Option(undefined, { id: '30', label: '1 month' }),
            new Option(undefined, { id: '35', label: '5 weeks' }),
            new Option(undefined, { id: '40', label: '40 days' }),
            new Option(undefined, { id: '42', label: '6 weeks' }),
            new Option(undefined, { id: '45', label: '45 days' }),
            new Option(undefined, { id: '56', label: '8 weeks' }),
            new Option(undefined, { id: '60', label: '2 months' }),
            new Option(undefined, { id: '70', label: '10 weeks' }),
            new Option(undefined, { id: '90', label: '3 months' }),
            new Option(undefined, { id: '105', label: '15 weeks' }),
            new Option(undefined, { id: '119', label: '17 weeks' }),
            new Option(undefined, { id: '120', label: '4 months' }),
            new Option(undefined, { id: '150', label: '5 months' }),
            new Option(undefined, { id: '180', label: '6 months' }),
            new Option(undefined, { id: '210', label: '7 months' }),
            new Option(undefined, { id: '240', label: '8 months' }),
            new Option(undefined, { id: '270', label: '9 months' }),
            new Option(undefined, { id: '360', label: '12 months' }),
            new Option(undefined, { id: '540', label: '18 months' }),
            new Option(undefined, { id: '720', label: '24 months' }),
            new Option(undefined, { id: '1080', label: '36 months' }),
            new Option(undefined, { id: '1800', label: '5 years' }),
            new Option(undefined, { id: '3600', label: '10 years' }),
        ]
        return strings
    }

    static capacityDisplayString(type: string, capacity: number): string | undefined {
        return capacity !== undefined
            ? type === 'session'
                ? capacity === 1
                    ? '1 session'
                    : `${capacity} sessions`
                : `${capacity}h`
            : undefined
    }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0) return (this.total ?? 0) - this.discountAmount
        return this.total
    }

    get discountAmount(): number {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            if (this.discountType === 'percent')
                discount = currencyFloat((this.discount * (this.total ?? 0)) / 100)
            return discount
        }
        return 0
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge && !this.markPaidDate) {
            return (
                (this.taxAmount + (this.amount ?? 0) + this.gratuityAmount) *
                (Source.surchargeRate(this.source) ?? 0)
            )
        }

        return 0
    }

    get taxAmount(): number {
        if (this.tax && this.tax > 0 && this.amount !== undefined)
            return currencyFloat((this.tax * this.amount) / 100)
        return 0
    }

    get chargeAmount(): number {
        return (this.amount ?? 0) + this.taxAmount + this.gratuityAmount + this.surchargeAmount
    }

    get askingAmount(): number {
        return (this.amount ?? 0) + this.taxAmount
    }

    get allowAnyPayment(): string | undefined {
        return this.maxPaymentCount !== undefined
            ? `${this.maxPaymentCount}_${this.minPaymentPercent}`
            : undefined
    }

    get capacityString(): string | undefined {
        return this.capacity !== undefined
            ? this.capacityType === 'session'
                ? this.capacity.toString()
                : `${this.capacity}h`
            : undefined
    }

    get capacityDisplayString(): string | undefined {
        return Package.capacityDisplayString(this.capacityType!, this.capacity!)
    }

    async available(): Promise<number | undefined> {
        if (this.id) {
            const sql = new SQLBuilder()
            sql.addField('count(*)', 'ct')
            sql.addField('coalesce(sum(duration),0)', 'duration')
            sql.addTable('bookings')
            sql.filterBy("bookings.status != 'waitListed'")
            sql.filterBy('bookings.packageCancelledDate IS NULL')
            sql.filterBy('package=?', this.id)
            const result = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
            if (this.capacityType === 'session') return this.capacity! - result['ct']
            return this.capacity! - result['duration']
        }
        return this.capacity
    }

    set allowAnyPayment(allowAnyPayment: string | undefined) {
        if (allowAnyPayment) {
            const parts = allowAnyPayment.split('_')
            this.maxPaymentCount = parseInt(parts[0])
            this.minPaymentPercent = parseInt(parts[1])
        }
    }

    get person(): User | undefined {
        if (PSApplication.me().id === this.customer?.id) return this.employee ?? this.vendor
        return Transaction.getPerson(this)
    }

    get paidAmount(): number | undefined {
        let paidAmount = this._paidAmount ?? 0
        if (this.markPaidDate) {
            let amount = this.amount !== undefined ? this.amount - paidAmount : 0
            if (this.gratuityAmount > 0) amount += this.gratuityAmount
            if (this.taxAmount > 0) amount += this.taxAmount
            return amount
        }
        return paidAmount > 0 ? paidAmount : undefined
    }

    get gratuityAmount(): number {
        if (this.gratuity && this.gratuity > 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent')
                if (this.amount === undefined) gratuity = 0
                else gratuity = currencyFloat((this.gratuity * this.amount) / 100)
            return gratuity
        }
        return 0
    }

    get owedAmount(): number | undefined {
        let paidAmount = this._paidAmount ?? 0
        return this.paidDate !== undefined || this.cancelledDate !== undefined
            ? undefined
            : (this.amount ?? 0) - paidAmount
    }

    get statusString(): string {
        if (!this.id && this.status === 'paid') return 'Already paid'
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.markPaidDate && this.paymeth)
            return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
        if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
        if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
        if (this.completedDate) return `Completed ${this.completedDate.format('MMM D')}`
        if (this.id && this.paidAmount)
            return `Paid ${this.paidAmount} (${this.remainingBalance} left)`
        if (this.id) return `Sent ${this.createdDate!.format('MMM D')} - not paid`
        return 'Requires payment'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: 'Sent',
                date: this.createdDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: 'Paid',
                date: this.paidDate,
                intent: 'success',
            },
            this.cancelledDate
                ? {
                      title: 'Cancelled',
                      date: this.cancelledDate,
                      intent: 'danger',
                  }
                : this.expiredDate && this.expiredDate.isBefore(moment())
                  ? {
                        title: 'Expired',
                        date: this.expiredDate,
                        intent: 'danger',
                    }
                  : null,
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    get dashboardStatusString(): string {
        if (this.markPaidDate)
            return `Paid via ${this.paymeth ?? 'other'} - exp ${this.expiredDate?.format('MMM D, YYYY')}`
        if (this.paidDate)
            return `Paid via credit card - exp ${this.expiredDate?.format('MMM D, YYYY')}`
        if (!this.createdDate) return ''
        return `Sent ${this.createdDate.format('MMM D')} - not paid`
    }

    async availableString(): Promise<string> {
        let available = await this.available()
        return `${this.capacityType === 'session' ? available : formatDuration(available, false)} left`
    }

    get expirationString(): string | undefined {
        if (this.paidDate) {
            if (this.expiredDate) {
                const isPassed = this.expiredDate.isBefore(moment())
                return `${isPassed ? 'Expired' : 'Expires'} on ${this.expiredDate.format('MMM D, YYYY')}`
            }
            else return 'Does not expire'
        }
        const l = Package.expirationStrings()
        let expires = this.expires
        for (let i = 0; i < l.length; i++) {
            const opt = l[i]
            if (opt.id === (expires?.toString() ?? '0')) return opt.label
        }
        return undefined
    }

    get name(): string | undefined {
        return this.item?.name
    }

    toString(): string {
        let description = ''
        if (this.cancelledDate) {
            description += `Canceled on ${this.cancelledDate.format('MMM D')}`
        }
        else if (this.completedDate) {
            description += `Completed on ${this.completedDate.format('MMM D')}`
        }
        else if (this.id && this.expiredDate)
            description += `expires ${this.expiredDate.format('MMM D, YYYY')}`
        else {
            let item = this.item!
            if (item.expires! % 30) description += `expires ${item.expires} days`
            else if (item.expires === 30) description += 'expires 1 month'
            else description += `expires ${item.expires! / 30} months`
        }
        return description
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.customer) this.customer = BaseRecord.fromSerializer(data.customer, User)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.promotion) this.promotion = BaseRecord.fromSerializer(data.promotion, Promotion)
        if (data.subscription)
            this.subscription = BaseRecord.fromSerializer(data.subscription, Subscription)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.expiredDate) this.expiredDate = moment(data.expiredDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        items: ['item'],
        gifts: ['gift'],
        users: ['customer'],
        promotions: ['promotion'],
        sources: ['source'],
        subscriptions: ['subscription'],
    })

    static dataType(): DataType {
        return 'packages'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['discount'] = this.discount
        dict['gratuity'] = this.gratuity ?? ''
        dict['gratuity_type'] = this.gratuity ? this.gratuityType : ''
        dict['expires'] = this.expires
        if (this.expiredDate) dict['expired_date'] = this.expiredDate
        if (!this.id) {
            dict['status'] = this.status
            if (this.customer) dict['customer'] = this.customer.id
        }
        dict['capacity'] = this.capacity
        dict['capacity_type'] = this.capacityType ?? ''
        dict['memo'] = this.memo ?? ''
        dict['item'] = this.item?.id ?? ''
        dict['gift'] = this.gift?.id ?? ''
        dict['source'] = this.source?.id ?? ''
        dict['channel'] = this.channel ?? ''
        dict['reminder'] = this.reminder ?? '1'
        dict['promotion'] = this.promotion?.id ?? ''
        dict['discount_type'] = this.discountType ?? ''
        dict['allow_surcharge'] = this.allowSurcharge
        dict['max_payment_count'] = this.maxPaymentCount
        dict['min_payment_percent'] = this.minPaymentPercent
        return dict
    }

    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get customer(): User | undefined {
        return this.getForeignRecord('customer', User)
    }
    set customer(customer: User | undefined) {
        this.setForeignRecord('customer', customer)
    }
    get subscription(): Subscription | undefined {
        return this.getForeignRecord('subscription', Subscription)
    }
    set subscription(subscription: Subscription | undefined) {
        this.setForeignRecord('subscription', subscription)
    }
    get promotion(): Promotion | undefined {
        return this.getForeignRecord('promotion', Promotion)
    }
    set promotion(promotion: Promotion | undefined) {
        this.setForeignRecord('promotion', promotion)
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
